import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import PageTitle from 'Components/PageTitle'
import StyledAccordion from 'Components/StyledAccordion'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import { AffiliateNotificationList } from 'Services/SalesManagement/Features/Notification/Components/AffiliateNotificationList'
import { AuthorityNotificationList } from 'Services/SalesManagement/Features/Notification/Components/AuthorityNotificationList'
import { WebShopNotificationList } from 'Services/SalesManagement/Features/Notification/Components/WebShopNotificationList'
import {
  fetchAffiliateNotificationList,
  fetchAffiliateNotificationOperators,
  fetchAuthorityNotificationAuthorities,
  fetchAuthorityNotificationList,
  fetchWebShopNotificationAuthorities,
  fetchWebShopNotificationList,
  type NotificationParamShape,
  NotificationType,
  type NotificationTypeOptions
} from 'Services/SalesManagement/Features/Notification/notificationSlice'
import useSalesManagementBreadcrumbs from 'Services/SalesManagement/Hooks/useSalesManagementBreadcrumbs'
import useTypedSelector, { useAppDispatch } from 'Store'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import useQuery from 'Util/getQueryParamsHook'
import useHash from 'Util/hooks/useHash'
import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FilterListIcon from '@mui/icons-material/FilterList'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import {
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Tab,
  TextField
} from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export function NotificationListPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/notifications`
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const queryParams = useQuery()
  const hash = useHash()
  const {
    webShopNotifications,
    affiliateNotifications,
    affiliateNotificationOperators,
    affiliateNotificationOperatorsLoading,
    webShopNotificationAuthorities,
    webShopNotificationAuthoritiesLoading,
    authorityNotificationAuthorities,
    authorityNotificationAuthoritiesLoading,
    authorityNotifications,
    count,
    loading
  } = useTypedSelector(state => state.notification)
  const { user } = useTypedSelector(state => state.user)
  const [tabValue, setTabValue] = useState<NotificationTypeOptions>(
    (hash as NotificationType) || NotificationType.webShop
  )

  const authorityParam = (() => {
    const initialAuthorityQueryParam = queryParams.get('authority') as string
    const initialAuthorityQueryParamAsNumber = Number.parseInt(initialAuthorityQueryParam, 10)
    if (initialAuthorityQueryParam === 'general') {
      return initialAuthorityQueryParam
    } else if (Number.isNaN(initialAuthorityQueryParamAsNumber)) {
      return ''
    } else {
      return initialAuthorityQueryParamAsNumber
    }
  })()

  const generalAuthorityChoiceValue = 'general'
  const generalAuthorityChoiceLabel = t`General`

  const [params, setParams] = useState<NotificationParamShape>({
    ordering: queryParams.get('ordering') ?? '-created',
    page: queryParams.get('page') ? Number.parseInt(queryParams.get('page') as string, 10) : 1,
    editor: queryParams.get('editor') ?? '',
    operator: queryParams.get('operator')
      ? Number.parseInt(queryParams.get('operator') as string, 10)
      : '',
    authority: authorityParam
  })

  const [filters, setFilters] = useState<Partial<NotificationParamShape>>({
    editor: params.editor,
    operator: params.operator,
    authority: params.authority
  })

  interface FilterState {
    affiliateNotificationOperatorsLoaded: boolean
    webShopNotificationAuthoritiesLoaded: boolean
    authorityNotificationAuthoritiesLoaded: boolean
  }
  const [filterState, setFilterState] = useState<FilterState>({
    affiliateNotificationOperatorsLoaded: false,
    webShopNotificationAuthoritiesLoaded: false,
    authorityNotificationAuthoritiesLoaded: false
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: setQueryParams changes on every re-render and should not be used as a hook dependency.
  useEffect(() => {
    switch (tabValue) {
      case NotificationType.webShop:
        dispatch(fetchWebShopNotificationList(params)).then(() => {
          setQueryParams()
        })
        break
      case NotificationType.affiliate:
        dispatch(fetchAffiliateNotificationList(params)).then(() => {
          setQueryParams()
        })
        break
      case NotificationType.authority:
        dispatch(fetchAuthorityNotificationList(params)).then(() => {
          setQueryParams()
        })
        break
      default:
        setTabValue(NotificationType.webShop)
        dispatch(fetchWebShopNotificationList(params)).then(() => {
          setQueryParams()
        })
    }
  }, [dispatch, params, tabValue])

  const clearFilters = () => {
    setFilters(prevState => ({ ...prevState, editor: '', operator: '', authority: '' }))
  }

  useEffect(() => {
    switch (tabValue) {
      case NotificationType.webShop:
        dispatch(fetchWebShopNotificationAuthorities())
        break
      case NotificationType.affiliate:
        dispatch(fetchAffiliateNotificationOperators())
        break
      case NotificationType.authority:
        dispatch(fetchAuthorityNotificationAuthorities())
        break
    }
  }, [tabValue, dispatch])

  useEffect(() => {
    if (webShopNotificationAuthoritiesLoading === 'fulfilled') {
      setFilterState(prevState => ({
        ...prevState,
        webShopNotificationAuthoritiesLoaded: true
      }))
    }
  }, [webShopNotificationAuthoritiesLoading])
  useEffect(() => {
    if (affiliateNotificationOperatorsLoading === 'fulfilled') {
      setFilterState(prevState => ({
        ...prevState,
        affiliateNotificationOperatorsLoaded: true
      }))
    }
  }, [affiliateNotificationOperatorsLoading])
  useEffect(() => {
    if (authorityNotificationAuthoritiesLoading === 'fulfilled') {
      setFilterState(prevState => ({
        ...prevState,
        authorityNotificationAuthoritiesLoaded: true
      }))
    }
  }, [authorityNotificationAuthoritiesLoading])

  const setQueryParams = () => {
    const query: string[] = []
    Object.keys(params).forEach((key: string) => {
      const value = encodeURIComponent(params[key as keyof NotificationParamShape])
      if (value) {
        query.push(`${key}=${value}`)
      }
    })
    const queryString = query.join('&')

    navigate({
      pathname: location.pathname,
      search: `?${queryString}`,
      hash: tabValue
    })
  }
  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = params.ordering.includes('-')
    setParams(prevState => ({ ...prevState, ordering: `${isAsc ? '' : '-'}${property}` }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({ ...prevState, page: newPage + 1 }))
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: NotificationType) => {
    navigate(`#${newValue}`)
    clearFilters()
    setTabValue(newValue)
  }
  const handleFilterByEditorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const editor = event.target.value
    setFilters({ ...filters, editor: editor })
  }
  const showMyNotifications = () => {
    if (user !== null) {
      setFilters({ ...filters, editor: user.email })
    }
  }
  const handleFilterByAffiliateNotificationOperator = (event: SelectChangeEvent) => {
    const operator = event.target.value !== '' ? Number.parseInt(event.target.value, 10) : ''
    setFilters({ ...filters, operator: operator })
  }
  const handleFilterByAuthority = (event: SelectChangeEvent) => {
    const value = event.target.value
    const authority = (() => {
      if (value !== '' && value !== generalAuthorityChoiceValue) {
        return Number.parseInt(value, 10)
      } else {
        return value
      }
    })()
    setFilters({ ...filters, authority: authority })
  }
  const submitFilters = () => {
    setParams(prevState => ({ ...prevState, ...filters, page: 1 }))
  }
  const breadcrumbs = useSalesManagementBreadcrumbs([{ title: t`Notifications` }])
  const isAdmin = isSuperuser(user)
  const [expandFilterSection, setExpandFilterSection] = useState(false)
  const onChangeFilterSectionVisibility = (_event: React.SyntheticEvent, expanded: boolean) => {
    setExpandFilterSection(expanded)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <BackButton link={SALES_MANAGEMENT_ROOT_URL} />
        <PageTitle>{t`Notifications`}</PageTitle>
        <Typography component="p" variant="subtitle1" sx={{ fontSize: '15px', mb: 4 }}>
          {t`In the notification section you can create, read, update and delete notifications for Waltti webshop, Waltti Mobile and Waltti points of sale.`}
        </Typography>
      </Box>
      <TabContext value={tabValue}>
        <Paper>
          <TabList onChange={handleTabChange} aria-label={t`Notification tab list`}>
            <Tab label={t`Waltti webshop`} value={NotificationType.webShop} />
            <Tab label={t`Affiliate notifications`} value={NotificationType.affiliate} />
            {isAdmin && (
              <Tab label={t`Authority notifications`} value={NotificationType.authority} />
            )}
          </TabList>
        </Paper>
        <TabPanel sx={{ px: 0, pt: 1 }} value={NotificationType.webShop}>
          <CustomButton
            sx={{ mt: 1, mb: 2 }}
            onClick={() => navigate(`${baseUrl}/webshop/create/`)}
            size="small"
            startIcon={<AddIcon />}
          >
            {t`Create new Waltti webshop notification`}
          </CustomButton>
          {isAdmin && (
            <StyledAccordion
              sx={{ mb: 3 }}
              expanded={expandFilterSection}
              onChange={onChangeFilterSectionVisibility}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <FilterListIcon sx={{ mr: 1 }} color="primary" />
                <Typography component="span">{t`Filters`}</Typography>
              </AccordionSummary>
              <Divider />
              <AccordionDetails>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    component="h3"
                    variant="h3"
                    sx={{ my: 2 }}
                  >{t`Filter by author or editor`}</Typography>
                  <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
                    <TextField
                      size="small"
                      label={t`Author or editor email address`}
                      sx={{ minWidth: '325px', backgroundColor: 'background.default' }}
                      value={filters.editor}
                      onChange={handleFilterByEditorChange}
                    />
                  </FormControl>
                  <CustomButton
                    sx={{ display: 'block', mb: 2 }}
                    onClick={showMyNotifications}
                    size="small"
                  >
                    {t`Set as my email address`}
                  </CustomButton>
                </Box>
                {filterState.webShopNotificationAuthoritiesLoaded && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      component="h3"
                      variant="h3"
                      sx={{ my: 2 }}
                    >{t`Filter by fields`}</Typography>
                    <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
                      <InputLabel>{t`Authority`}</InputLabel>
                      <Select
                        sx={{ backgroundColor: 'background.default' }}
                        label={t`Authority`}
                        autoWidth
                        value={filters.authority as string}
                        onChange={handleFilterByAuthority}
                      >
                        <MenuItem value="">---------</MenuItem>
                        <MenuItem value={generalAuthorityChoiceValue}>
                          {generalAuthorityChoiceLabel}
                        </MenuItem>
                        {webShopNotificationAuthorities.map(authority => (
                          <MenuItem
                            key={'webshop-notification-authority-choice-'.concat(
                              authority.id.toString()
                            )}
                            value={authority.id}
                          >
                            {authority.authority_label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                <Box sx={{ mt: 3 }}>
                  <CustomButton sx={{ mr: 2 }} onClick={submitFilters}>{t`Filter`}</CustomButton>
                  <Button onClick={clearFilters}>{t`Clear filters`}</Button>
                </Box>
              </AccordionDetails>
            </StyledAccordion>
          )}
          <WebShopNotificationList
            rows={webShopNotifications}
            loading={loading}
            params={params}
            handleRequestSort={handleRequestSort}
            handleChangePage={handleChangePage}
            count={count}
          />
        </TabPanel>
        <TabPanel sx={{ px: 0, pt: 1 }} value={NotificationType.affiliate}>
          <CustomButton
            sx={{ mt: 1, mb: 2 }}
            onClick={() => navigate(`${baseUrl}/affiliate/create/`)}
            size="small"
            startIcon={<AddIcon />}
          >
            {t`Create new affiliate notification`}
          </CustomButton>
          {isAdmin && (
            <StyledAccordion
              sx={{ mb: 3 }}
              expanded={expandFilterSection}
              onChange={onChangeFilterSectionVisibility}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <FilterListIcon sx={{ mr: 1 }} color="primary" />
                <Typography component="span">{t`Filters`}</Typography>
              </AccordionSummary>
              <Divider />
              <AccordionDetails>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    component="h3"
                    variant="h3"
                    sx={{ my: 2 }}
                  >{t`Filter by author or editor`}</Typography>
                  <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
                    <TextField
                      size="small"
                      label={t`Author or editor email address`}
                      helperText=""
                      sx={{ minWidth: '325px', backgroundColor: 'background.default' }}
                      value={filters.editor}
                      onChange={handleFilterByEditorChange}
                    />
                  </FormControl>
                  <CustomButton
                    sx={{ display: 'block', mb: 2 }}
                    onClick={showMyNotifications}
                    size="small"
                  >
                    {t`Set as my email address`}
                  </CustomButton>
                </Box>
                {filterState.affiliateNotificationOperatorsLoaded && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      component="h3"
                      variant="h3"
                      sx={{ my: 2 }}
                    >{t`Filter by fields`}</Typography>
                    <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
                      <InputLabel>{t`Operator`}</InputLabel>
                      <Select
                        sx={{ minWidth: '325px', backgroundColor: 'background.default' }}
                        label={t`Operator`}
                        autoWidth
                        value={filters.operator as string}
                        onChange={handleFilterByAffiliateNotificationOperator}
                      >
                        <MenuItem value="">---------</MenuItem>
                        {affiliateNotificationOperators.map(operator => (
                          <MenuItem
                            key={'operator-choice-'.concat(operator.id.toString())}
                            value={operator.id}
                          >
                            {operator.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                <Box sx={{ mt: 3 }}>
                  <CustomButton sx={{ mr: 2 }} onClick={submitFilters}>{t`Filter`}</CustomButton>
                  <Button onClick={clearFilters}>{t`Clear filters`}</Button>
                </Box>
              </AccordionDetails>
            </StyledAccordion>
          )}
          <AffiliateNotificationList
            rows={affiliateNotifications}
            loading={loading}
            params={params}
            handleRequestSort={handleRequestSort}
            handleChangePage={handleChangePage}
            count={count}
          />
        </TabPanel>
        {isAdmin && (
          <TabPanel sx={{ px: 0, pt: 1 }} value={NotificationType.authority}>
            <CustomButton
              sx={{ mt: 1, mb: 2 }}
              onClick={() => navigate(`${baseUrl}/authority/create/`)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new authority notification`}
            </CustomButton>
            <StyledAccordion
              sx={{ mb: 3 }}
              expanded={expandFilterSection}
              onChange={onChangeFilterSectionVisibility}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <FilterListIcon sx={{ mr: 1 }} color="primary" />
                <Typography component="span">{t`Filters`}</Typography>
              </AccordionSummary>
              <Divider />
              <AccordionDetails>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    component="h3"
                    variant="h3"
                    sx={{ my: 2 }}
                  >{t`Filter by author or editor`}</Typography>
                  <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
                    <TextField
                      size="small"
                      label={t`Author or editor email address`}
                      helperText=""
                      sx={{ minWidth: '325px', backgroundColor: 'background.default' }}
                      value={filters.editor}
                      onChange={handleFilterByEditorChange}
                    />
                  </FormControl>
                  <CustomButton
                    sx={{ display: 'block', mb: 2 }}
                    onClick={showMyNotifications}
                    size="small"
                  >
                    {t`Set as my email address`}
                  </CustomButton>
                </Box>
                {filterState.authorityNotificationAuthoritiesLoaded && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      component="h3"
                      variant="h3"
                      sx={{ my: 2 }}
                    >{t`Filter by fields`}</Typography>
                    <FormControl sx={{ mr: 1, minWidth: 180, mb: 1 }} size="small">
                      <InputLabel>{t`Authority`}</InputLabel>
                      <Select
                        sx={{ backgroundColor: 'background.default' }}
                        label={t`Authority`}
                        autoWidth
                        value={filters.authority as string}
                        onChange={handleFilterByAuthority}
                      >
                        <MenuItem value="">---------</MenuItem>
                        <MenuItem value={generalAuthorityChoiceValue}>
                          {generalAuthorityChoiceLabel}
                        </MenuItem>
                        {authorityNotificationAuthorities.map(authority => (
                          <MenuItem
                            key={'authority-notification-authority-choice-'.concat(
                              authority.id.toString()
                            )}
                            value={authority.id}
                          >
                            {authority.authority_label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                <Box sx={{ mt: 3 }}>
                  <CustomButton sx={{ mr: 2 }} onClick={submitFilters}>{t`Filter`}</CustomButton>
                  <Button onClick={clearFilters}>{t`Clear filters`}</Button>
                </Box>
              </AccordionDetails>
            </StyledAccordion>
            <AuthorityNotificationList
              rows={authorityNotifications}
              loading={loading}
              params={params}
              handleRequestSort={handleRequestSort}
              handleChangePage={handleChangePage}
              count={count}
            />
          </TabPanel>
        )}
      </TabContext>
    </Box>
  )
}
